import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { UnderwritingOffer } from '_gql'

import { useCanary } from '@bill/canary.react'
import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import Spinner from '@divvy-web/skylab.spinner'

import BillBrandLogo from '@sputnik/components/BillBrandLogo'
import useDivvyUUID from '@sputnik/hooks/useDivvyUUID'
import useGetInfoForApplicationAgreements from '@sputnik/hooks/useGetInfoForApplicationAgreements'
import CreditLineOffersPage from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage'
import { URL } from '@sputnik/resources/constants'

import EligibleOffersPageFooter from './EligibleOffersPageFooter'
import EligibleOfferTile from './EligibleOfferTile'
import { GetEligibleOffers_decisions_Decision, useGetEligibleOffers } from './GetEligibleOffers.gql'
import IncludedWithOffers from './IncludedWithOffers'
import styles from './styles.module.scss'

const isAutoSelectEligible = (
  decision: GetEligibleOffers_decisions_Decision | null | undefined,
  {
    hasAgreedToTila,
    hasTilaContent,
    isLoading,
  }: { hasAgreedToTila: boolean; hasTilaContent: boolean; isLoading: boolean },
  hasOneOffer: boolean,
) => {
  if (!decision) {
    return false
  }

  // already eligible if the tila has already been signed
  if (hasAgreedToTila) {
    return true
  }

  // eligible if it doesn't require a tila and is the only offer, or is the most popular offer
  return !isLoading && !hasTilaContent && (hasOneOffer || decision.offerType === UnderwritingOffer.FIXED_LINE)
}

const EligibleOffersPage = () => {
  const [getClassName] = useNameFormatter('EligibleOffersPage')
  const isUWPageRedesign = useCanary('rollout-uw-page-redesign')
  const [companyDivvyUuid] = useDivvyUUID()
  const { appId } = useParams()

  const { data: decisionsData, loading: isLoadingDecisionData } = useGetEligibleOffers({
    skip: !companyDivvyUuid || !isUWPageRedesign || !appId,
    variables: { companyId: companyDivvyUuid },
  })

  const decisions = decisionsData?.decisions || []
  const variableLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.VARIABLE_LINE)
  const standardLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.FIXED_LINE)
  const securedLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.SECURED_LINE)

  const hasStandardOffer = !!standardLineOffer
  const hasVariableOffer = !!variableLineOffer
  const hasSecuredOffer = !!securedLineOffer

  const offerCount = [hasStandardOffer, hasVariableOffer, hasSecuredOffer].filter(Boolean).length
  const hasOneOffer = offerCount === 1

  const [manuallySelectedOffer, setManuallySelectedOffer] = useState<
    GetEligibleOffers_decisions_Decision | null | undefined
  >(null)

  const variableOfferAgreementInfo = useGetInfoForApplicationAgreements({
    decision: variableLineOffer,
    hasAgreedToTerms: false,
    isLoadingAcceptingOffer: false,
    skip: !isUWPageRedesign,
  })
  const standardOfferAgreementInfo = useGetInfoForApplicationAgreements({
    decision: standardLineOffer,
    hasAgreedToTerms: false,
    isLoadingAcceptingOffer: false,
    skip: !isUWPageRedesign,
  })
  const securedOfferAgreementInfo = useGetInfoForApplicationAgreements({
    decision: securedLineOffer,
    hasAgreedToTerms: false,
    isLoadingAcceptingOffer: false,
    skip: !isUWPageRedesign,
  })

  const autoSelectedOffer = useMemo(
    () =>
      [
        { decision: variableLineOffer, info: variableOfferAgreementInfo },
        { decision: standardLineOffer, info: standardOfferAgreementInfo },
        { decision: securedLineOffer, info: securedOfferAgreementInfo },
      ].find(({ decision, info }) => isAutoSelectEligible(decision, info, hasOneOffer))?.decision,
    [
      variableLineOffer,
      variableOfferAgreementInfo,
      standardLineOffer,
      standardOfferAgreementInfo,
      securedLineOffer,
      securedOfferAgreementInfo,
      hasOneOffer,
    ],
  )

  const selectedOffer = manuallySelectedOffer ?? autoSelectedOffer

  if (!isUWPageRedesign) {
    return <CreditLineOffersPage />
  }

  if (!decisionsData || isLoadingDecisionData) {
    return (
      <div>
        <Spinner centered />
      </div>
    )
  }

  return (
    <div className={`${styles[getClassName('')]} fs-unmask`}>
      <BillBrandLogo />
      <div className={styles[getClassName('content')]}>
        <div className={styles[getClassName('title')]}>
          <FormattedMessage
            defaultMessage='Congrats, you are eligible for {numOffers, plural, one {# offer} other {# offers}}.'
            id='sputnik.EligibleOffersPage__/q5IXg'
            values={{ numOffers: offerCount }}
          />
        </div>
        <div className={styles[getClassName('subtitle')]}>
          <FormattedMessage
            defaultMessage='Based on the information provided from you application.'
            id='sputnik.EligibleOffersPage__bpaGwn'
          />
        </div>
        <div className={styles[getClassName('tiles')]}>
          {hasVariableOffer && (
            <EligibleOfferTile
              bulletPoints={[
                <FormattedMessage
                  key='credit-limit-fluctuate'
                  defaultMessage='Your credit limit may fluctuate based on the cash balance in your connected bank accounts'
                  id='sputnik.EligibleOffersPage__GiJHAo'
                />,
                <FormattedMessage
                  key='secure-digital-banking'
                  defaultMessage="We'll use a secure digital banking connection to review your account balances and determine any changes to your credit limit"
                  id='sputnik.EligibleOffersPage__3/EfUe'
                />,
                <FormattedMessage
                  key='balance-due-in-full'
                  defaultMessage='Your balance is due in full at the end of each billing cycle'
                  id='sputnik.EligibleOffersPage__lysSKm'
                />,
              ]}
              creditApplication={variableOfferAgreementInfo?.creditApplication}
              decision={variableLineOffer}
              description={
                <FormattedMessage
                  defaultMessage='For high-growth businesses that need a limit that scales with their cash flow'
                  id='sputnik.EligibleOffersPage__229Q7f'
                />
              }
              hasAgreedToTila={variableOfferAgreementInfo?.hasAgreedToTila}
              hasTilaContent={variableOfferAgreementInfo?.hasTilaContent}
              isSelected={selectedOffer?.offerType === UnderwritingOffer.VARIABLE_LINE}
              learnMoreLink={URL.VARIABLE_LINE_OFFER_LEARN_MORE_LINK}
              setSelectedOffer={setManuallySelectedOffer}
              title={
                <FormattedMessage
                  defaultMessage='Dynamic cash-based line'
                  id='sputnik.EligibleOffersPage__rkSl0s'
                />
              }
            />
          )}
          {hasStandardOffer && (
            <EligibleOfferTile
              bulletPoints={[
                <FormattedMessage
                  key='fixed-limit'
                  defaultMessage='Fixed limit that builds a long-term credit relationship over time'
                  id='sputnik.EligibleOffersPage__T4ZCvI'
                />,
                <FormattedMessage
                  key='keep-cash'
                  defaultMessage='Keep cash in your bank — pay on a schedule, not upfront'
                  id='sputnik.EligibleOffersPage__Gxov4L'
                />,
                <FormattedMessage
                  key='balance-due'
                  defaultMessage='Balance due in full at the end of each billing cycle'
                  id='sputnik.EligibleOffersPage__x8jXFJ'
                />,
              ]}
              creditApplication={standardOfferAgreementInfo?.creditApplication}
              decision={standardLineOffer!}
              description={
                <FormattedMessage
                  defaultMessage='For established businesses that want predictable, steady access to credit'
                  id='sputnik.EligibleOffersPage__ZRWHX2'
                />
              }
              hasAgreedToTila={standardOfferAgreementInfo?.hasAgreedToTila}
              hasTilaContent={standardOfferAgreementInfo?.hasTilaContent}
              isSelected={selectedOffer?.offerType === UnderwritingOffer.FIXED_LINE}
              learnMoreLink={URL.STANDARD_LINE_OFFER_LEARN_MORE_LINK}
              setSelectedOffer={setManuallySelectedOffer}
              title={
                <FormattedMessage
                  defaultMessage='Fixed credit line'
                  id='sputnik.EligibleOffersPage__oWLr0e'
                />
              }
            />
          )}
          {hasSecuredOffer && (
            <EligibleOfferTile
              bulletPoints={[
                <FormattedMessage
                  key='spend-only'
                  defaultMessage="Spend only what you've prefunded — no overspending or debt risk"
                  id='sputnik.EligibleOffersPage__q8+k3I'
                />,
                <FormattedMessage
                  key='same-budget'
                  defaultMessage='Same budgets, policies, and controls as credit-line customers'
                  id='sputnik.EligibleOffersPage__BcCU5Q'
                />,
                <FormattedMessage
                  key='earn-minimum'
                  defaultMessage='Earn minimum 1.5x points on every dollar — no minimums or restrictions'
                  id='sputnik.EligibleOffersPage__bxZUyw'
                />,
              ]}
              creditApplication={securedOfferAgreementInfo?.creditApplication}
              decision={securedLineOffer!}
              description={
                <FormattedMessage
                  defaultMessage='For new businesses building credit or companies maximizing rewards'
                  id='sputnik.EligibleOffersPage__Qdr3fF'
                />
              }
              hasAgreedToTila={securedOfferAgreementInfo?.hasAgreedToTila}
              hasTilaContent={securedOfferAgreementInfo?.hasTilaContent}
              isSelected={selectedOffer?.offerType === UnderwritingOffer.SECURED_LINE}
              learnMoreLink={URL.PREPAY_OFFER_LEARN_MORE_LINK}
              setSelectedOffer={setManuallySelectedOffer}
              subtitle={
                <FormattedMessage
                  defaultMessage='You choose the credit limit'
                  id='sputnik.EligibleOffersPage__JxIS4R'
                />
              }
              title={
                <FormattedMessage
                  defaultMessage='Pre-fund'
                  id='sputnik.EligibleOffersPage__yXk7XL'
                />
              }
            />
          )}
        </div>
        <IncludedWithOffers />
        <EligibleOffersPageFooter selectedOffer={selectedOffer} />
      </div>
    </div>
  )
}

export default EligibleOffersPage
